import type {
  AddressForNewBookingListApiData,
  AddressForNewBookingListApiResponses,
  BookingForCustomerListApiData,
  BookingForCustomerListApiResponses,
  CarForNewBookingListApiData,
  CarForNewBookingListApiResponses,
  CarNotificationDefaultsGetApiData,
  CarNotificationDefaultsGetApiResponses,
  CarsAddToUserGroupApiData,
  CarsAddToUserGroupApiResponses,
  CarUpcomingBookingCheckApiData,
  CarUpcomingBookingCheckApiResponses,
  MembershipProgramListForUserGroupApiData,
  MembershipProgramListForUserGroupApiResponses,
  UserGroupAddressCreateApiData,
  UserGroupAddressCreateApiResponses,
  UserGroupAddressDeleteApiData,
  UserGroupAddressDeleteApiResponses,
  UserGroupAddressListApiData,
  UserGroupAddressListApiResponses,
  UserGroupAddressPatchApiData,
  UserGroupAddressPatchApiResponses,
  UserGroupCarForBookingListApiData,
  UserGroupCarForBookingListApiResponses,
  UserGroupCarForTireOfferListApiData,
  UserGroupCarForTireOfferListApiResponses,
  UserGroupCarGetApiData,
  UserGroupCarGetApiResponses,
  UserGroupCarManagerPatchApiData,
  UserGroupCarManagerPatchApiResponses,
  UserGroupCarsListApiData,
  UserGroupCarsListApiResponses,
  UserGroupCohortListApiData,
  UserGroupCohortListApiResponses,
  UserGroupCouponListApiData,
  UserGroupCouponListApiResponses,
  UserGroupCreateApiData,
  UserGroupCreateApiResponses,
  UserGroupDeleteApiData,
  UserGroupDeleteApiResponses,
  UserGroupEventsListApiData,
  UserGroupEventsListApiResponses,
  UserGroupExportExcelApiData,
  UserGroupExportExcelApiResponses,
  UserGroupFilterCountsApiData,
  UserGroupFilterCountsApiResponses,
  UserGroupForCustomerGetApiData,
  UserGroupForCustomerGetApiResponses,
  UserGroupGetApiData,
  UserGroupGetApiResponses,
  UserGroupGetReferralRewardsApiData,
  UserGroupGetReferralRewardsApiResponses,
  UserGroupListApiData,
  UserGroupListApiResponses,
  UserGroupListMinimalApiData,
  UserGroupListMinimalApiResponses,
  UserGroupMemberAddByPhoneApiData,
  UserGroupMemberAddByPhoneApiResponses,
  UserGroupMemberCreateApiData,
  UserGroupMemberCreateApiResponses,
  UserGroupMemberDeleteApiData,
  UserGroupMemberDeleteApiResponses,
  UserGroupMemberGetApiData,
  UserGroupMemberGetApiResponses,
  UserGroupMemberListApiData,
  UserGroupMemberListApiResponses,
  UserGroupMemberLookupApiData,
  UserGroupMemberLookupApiResponses,
  UserGroupMemberPatchApiData,
  UserGroupMemberPatchApiResponses,
  UserGroupMemberRemovalSummaryApiData,
  UserGroupMemberRemovalSummaryApiResponses,
  UserGroupPatchApiData,
  UserGroupPatchApiResponses,
  UserGroupReferredFriendsApiData,
  UserGroupReferredFriendsApiResponses,
  UserGroupServiceOrganizationCreateApiData,
  UserGroupServiceOrganizationCreateApiResponses,
  UserGroupSmsSendApiData,
  UserGroupSmsSendApiResponses,
  UserGroupTabCountsApiData,
  UserGroupTabCountsApiResponses
} from '@navio/nsdk';

import { downloadToExcel } from '../../downloadToExcel';
import { apiUrl, dataEndpoint, responseEndpoint } from '../defineResource';
import { convertToOrganizationUrls } from './convertToOrganizationUrls';
import { managerOverviewUrls } from './managerOverviewUrls';
import { notificationOverviewUrls } from './notificationOverviewUrls';
import { serviceOrganizationLinkUrls } from './serviceOrganizationLinkUrls';
import { userGroupCarOwnershipUrls } from './userGroupCarOwnershipResources';
import { userGroupNoteUrls } from './userGroupNoteUrls';

type UserGroupListQuery = NonNullable<UserGroupListApiData['query']>;
type UserGroupListApiDataWithCarCount = Omit<UserGroupListApiData, 'query'> & {
  query?: Omit<UserGroupListQuery, 'ordering'> & {
    ordering?: Array<NonNullable<UserGroupListQuery['ordering']>[number] | 'car_count'> | null;
  };
};
type UserGroupExportExcelQuery = NonNullable<UserGroupExportExcelApiData['query']>;
type UserGroupExportExcelApiDataWithCarCount = Omit<UserGroupExportExcelApiData, 'query'> & {
  query?: Omit<UserGroupExportExcelQuery, 'ordering'> & {
    ordering?: Array<NonNullable<UserGroupExportExcelQuery['ordering']>[number] | 'car_count'> | null;
  };
};

export const getUserGroupUrls = {
  getPaginatedUserGroups: dataEndpoint<UserGroupListApiDataWithCarCount, UserGroupListApiResponses>({
    getUrl: (queryParams) => apiUrl('user-groups/', queryParams)
  }),
  getUserGroupsExportExcel: dataEndpoint<UserGroupExportExcelApiDataWithCarCount, UserGroupExportExcelApiResponses>({
    getUrl: (queryParams) => apiUrl('user-groups/export-excel/', queryParams),
    handleRes: async (res) => downloadToExcel(res),
    responseType: 'blob',
    sideEffectGet: true
  }),
  getUserGroupFilterCounts: dataEndpoint<UserGroupFilterCountsApiData, UserGroupFilterCountsApiResponses>({
    getUrl: (queryParams) => apiUrl('user-groups/filter-counts/', queryParams)
  }),
  getPaginatedMinimalUserGroups: dataEndpoint<UserGroupListMinimalApiData, UserGroupListMinimalApiResponses>({
    getUrl: (queryParams) => apiUrl('user-groups/minimal/', queryParams)
  }),
  getUserGroup: dataEndpoint<UserGroupGetApiData, UserGroupGetApiResponses>({
    getUrl: ({ userGroupId }) => apiUrl(`user-groups/${userGroupId}/`)
  }),
  getUserGroupTabCounts: dataEndpoint<UserGroupTabCountsApiData, UserGroupTabCountsApiResponses>({
    getUrl: ({ userGroupId, ...queryParams }) => apiUrl(`user-groups/${userGroupId}/tab-counts/`, queryParams)
  }),
  getPaginatedUserGroupEvents: dataEndpoint<UserGroupEventsListApiData, UserGroupEventsListApiResponses>({
    getUrl: ({ userGroupId, ...queryParams }) => apiUrl(`user-groups/${userGroupId}/events/`, queryParams)
  }),
  postUserGroup: responseEndpoint<UserGroupCreateApiData, UserGroupCreateApiResponses>({
    getUrl: () => apiUrl('user-groups/'),
    getBody: (data) => data
  }),
  patchUserGroup: responseEndpoint<UserGroupPatchApiData, UserGroupPatchApiResponses>({
    getUrl: ({ userGroupId }) => apiUrl(`user-groups/${userGroupId}/`),
    getBody: ({ userGroupId: _, ...body }) => body
  }),
  ...convertToOrganizationUrls,
  deleteUserGroup: dataEndpoint<UserGroupDeleteApiData, UserGroupDeleteApiResponses>({
    getUrl: ({ userGroupId }) => apiUrl(`user-groups/${userGroupId}/`)
  }),
  getUserGroupCohorts: dataEndpoint<UserGroupCohortListApiData, UserGroupCohortListApiResponses>({
    getUrl: (queryParams) => apiUrl('user-group-cohorts/', queryParams)
  }),
  getPaginatedUserGroupMembers: dataEndpoint<UserGroupMemberListApiData, UserGroupMemberListApiResponses>({
    getUrl: (queryParams) => apiUrl('user-group-members/', queryParams)
  }),
  getUserGroupMember: dataEndpoint<UserGroupMemberGetApiData, UserGroupMemberGetApiResponses>({
    getUrl: ({ userGroupMemberId }) => apiUrl(`user-group-members/${userGroupMemberId}/`)
  }),
  postUserGroupMember: responseEndpoint<UserGroupMemberCreateApiData, UserGroupMemberCreateApiResponses>({
    getUrl: () => apiUrl('user-group-members/'),
    getBody: (data) => data
  }),
  patchUserGroupMember: responseEndpoint<UserGroupMemberPatchApiData, UserGroupMemberPatchApiResponses>({
    getUrl: ({ userGroupMemberId }) => apiUrl(`user-group-members/${userGroupMemberId}/`),
    getBody: ({ userGroupMemberId: _, ...body }) => body
  }),
  deleteUserGroupMember: dataEndpoint<UserGroupMemberDeleteApiData, UserGroupMemberDeleteApiResponses>({
    getUrl: ({ userGroupMemberId, ...queryParams }) => apiUrl(`user-group-members/${userGroupMemberId}/`, queryParams)
  }),
  getUserGroupMemberRemovalSummary: dataEndpoint<
    UserGroupMemberRemovalSummaryApiData,
    UserGroupMemberRemovalSummaryApiResponses
  >({
    getUrl: ({ userGroupMemberId }) => apiUrl(`user-group-members/${userGroupMemberId}/removal-summary/`)
  }),
  getUserGroupMemberLookup: dataEndpoint<UserGroupMemberLookupApiData, UserGroupMemberLookupApiResponses>({
    getUrl: ({ userGroupId, ...queryParams }) => apiUrl(`user-groups/${userGroupId}/members/lookup/`, queryParams)
  }),
  postUserGroupMemberAddByPhone: responseEndpoint<
    UserGroupMemberAddByPhoneApiData,
    UserGroupMemberAddByPhoneApiResponses
  >({
    getUrl: ({ userGroupId }) => apiUrl(`user-groups/${userGroupId}/members/add-by-phone/`),
    getBody: ({ userGroupId: _, ...body }) => body
  }),
  postUserGroupServiceOrganization: responseEndpoint<
    UserGroupServiceOrganizationCreateApiData,
    UserGroupServiceOrganizationCreateApiResponses
  >({
    getUrl: ({ userGroupId, serviceOrganizationId }) =>
      apiUrl(`user-groups/${userGroupId}/service-organizations/${serviceOrganizationId}/`)
  }),
  getPaginatedUserGroupMembershipProgram: dataEndpoint<
    MembershipProgramListForUserGroupApiData,
    MembershipProgramListForUserGroupApiResponses
  >({
    getUrl: ({ userGroupId, ...queryParams }) => apiUrl(`user-groups/${userGroupId}/membership-programs/`, queryParams)
  }),
  getUserGroupAddresses: dataEndpoint<UserGroupAddressListApiData, UserGroupAddressListApiResponses>({
    getUrl: (queryParams) => apiUrl('user-groups/addresses/', queryParams)
  }),
  postUserGroupAddress: responseEndpoint<UserGroupAddressCreateApiData, UserGroupAddressCreateApiResponses>({
    getUrl: ({ userGroupId }) => apiUrl(`user-groups/${userGroupId}/addresses/`),
    getBody: ({ userGroupId: _, ...body }) => body
  }),
  patchUserGroupAddress: responseEndpoint<UserGroupAddressPatchApiData, UserGroupAddressPatchApiResponses>({
    getUrl: ({ userGroupAddressId }) => apiUrl(`user-group-addresses/${userGroupAddressId}/`),
    getBody: ({ userGroupAddressId: _, ...body }) => body
  }),
  postDeleteUserGroupAddresses: responseEndpoint<UserGroupAddressDeleteApiData, UserGroupAddressDeleteApiResponses>({
    getUrl: ({ userGroupId }) => apiUrl(`user-groups/${userGroupId}/delete-addresses/`),
    getBody: ({ userGroupId: _, ...body }) => body
  }),
  getPaginatedUserGroupAddressesValidForNewBooking: dataEndpoint<
    AddressForNewBookingListApiData,
    AddressForNewBookingListApiResponses
  >({
    getUrl: ({ userGroupId, ...queryParams }) =>
      apiUrl(`user-groups/${userGroupId}/addresses-for-new-booking/`, queryParams)
  }),
  getUserGroupCoupons: dataEndpoint<UserGroupCouponListApiData, UserGroupCouponListApiResponses>({
    getUrl: ({ userGroupId, ...queryParams }) => apiUrl(`user-groups/${userGroupId}/coupons/`, queryParams)
  }),
  ...userGroupNoteUrls,
  getPaginatedUserGroupCarsForBooking: dataEndpoint<
    UserGroupCarForBookingListApiData,
    UserGroupCarForBookingListApiResponses
  >({
    getUrl: ({ userGroupId, ...queryParams }) => apiUrl(`user-groups/${userGroupId}/cars-for-booking/`, queryParams)
  }),
  getPaginatedUserGroupCarsForTireOffer: dataEndpoint<
    UserGroupCarForTireOfferListApiData,
    UserGroupCarForTireOfferListApiResponses
  >({
    getUrl: ({ userGroupId, ...queryParams }) => apiUrl(`user-groups/${userGroupId}/cars-for-tire-offer/`, queryParams)
  }),
  getPaginatedUserGroupCars: dataEndpoint<UserGroupCarsListApiData, UserGroupCarsListApiResponses>({
    getUrl: (queryParams) => apiUrl('user-group-cars/', queryParams)
  }),
  getUserGroupForCustomer: dataEndpoint<UserGroupForCustomerGetApiData, UserGroupForCustomerGetApiResponses>({
    getUrl: ({ userGroupId }) => apiUrl(`user-groups/${userGroupId}/for-customer/`)
  }),
  getReferrals: dataEndpoint<UserGroupGetReferralRewardsApiData, UserGroupGetReferralRewardsApiResponses>({
    getUrl: ({ userGroupId, ...queryParams }) => apiUrl(`user-groups/${userGroupId}/get-referral-rewards/`, queryParams)
  }),
  getReferredFriends: dataEndpoint<UserGroupReferredFriendsApiData, UserGroupReferredFriendsApiResponses>({
    getUrl: ({ userGroupId, ...queryParams }) => apiUrl(`user-groups/${userGroupId}/referred-friends/`, queryParams)
  }),
  getPaginatedBookingsForCustomer: dataEndpoint<BookingForCustomerListApiData, BookingForCustomerListApiResponses>({
    getUrl: ({ userGroupId, ...queryParams }) =>
      apiUrl(`user-groups/${userGroupId}/bookings-for-customer/`, queryParams)
  }),
  getCarUpcomingBookingCheck: dataEndpoint<CarUpcomingBookingCheckApiData, CarUpcomingBookingCheckApiResponses>({
    getUrl: ({ userGroupId, ...queryParams }) =>
      apiUrl(`user-groups/${userGroupId}/car-upcoming-booking-check/`, queryParams)
  }),
  postAddCarToUserGroup: responseEndpoint<CarsAddToUserGroupApiData, CarsAddToUserGroupApiResponses>({
    getUrl: ({ userGroupId }) => apiUrl(`user-groups/${userGroupId}/add-cars/`),
    getBody: (data) => data
  }),
  ...userGroupCarOwnershipUrls,
  getPaginatedCarsForBookingUserGroups: dataEndpoint<CarForNewBookingListApiData, CarForNewBookingListApiResponses>({
    getUrl: ({ userGroupId, ...queryParams }) => apiUrl(`user-groups/${userGroupId}/cars-for-new-booking/`, queryParams)
  }),
  getUserGroupCar: dataEndpoint<UserGroupCarGetApiData, UserGroupCarGetApiResponses>({
    getUrl: ({ userGroupId, carId }) => apiUrl(`user-groups/${userGroupId}/cars/${carId}/`)
  }),
  postUserGroupSMS: responseEndpoint<UserGroupSmsSendApiData, UserGroupSmsSendApiResponses>({
    getUrl: ({ userGroupId }) => apiUrl(`user-groups/${userGroupId}/sms-send/`),
    getBody: ({ userGroupId: _, ...body }) => body
  }),
  getCarNotificationDefaults: dataEndpoint<CarNotificationDefaultsGetApiData, CarNotificationDefaultsGetApiResponses>({
    getUrl: ({ userGroupId, ...queryParams }) =>
      apiUrl(`user-groups/${userGroupId}/car-notification-defaults/`, queryParams)
  }),
  ...notificationOverviewUrls,
  ...managerOverviewUrls,
  ...serviceOrganizationLinkUrls,
  patchUserGroupCarManagers: responseEndpoint<UserGroupCarManagerPatchApiData, UserGroupCarManagerPatchApiResponses>({
    getUrl: ({ userGroupId, userGroupCarId }) =>
      apiUrl(`user-groups/${userGroupId}/cars/${userGroupCarId}/car-managers/`),
    getBody: ({ userGroupId: _, userGroupCarId: __, ...body }) => body
  })
};
